import { latLngSchema, objectIdSchema, quantitySchema, vehicleTypesSchema } from '@hero/schema';
import { z } from 'zod';
import {
  API,
  CalculatePriceData,
  CalculateMultiDestinationPriceData,
  CalculatePriceAPIv2Data,
  CalculatePriceMultiRoutesData,
} from '../types';

export const calculatePriceSchema = z.object({
  origin: latLngSchema,
  destination: latLngSchema,
  vehicle: vehicleTypesSchema,
  /** quantity of vehicles */
  quantity: quantitySchema.optional(),
});

export type CalculatePriceAPI = API<z.infer<typeof calculatePriceSchema>, CalculatePriceData>;

//* multi distance price
const locationPriceSchema = z.object({
  /** the latitude and longitude of the location. */
  location: latLngSchema,
  /** if empty, then it means that the location is a `pickup/origin` location. */
  quantity: quantitySchema.optional(),
});

export const calculateMultiDistancePriceAPISchema = z.object({
  locations: z.array(locationPriceSchema).min(2),
  vehicle: vehicleTypesSchema,
});

export type CalculateMultiDistancePriceAPI = API<
  z.infer<typeof calculateMultiDistancePriceAPISchema>,
  CalculateMultiDestinationPriceData
>;

/**
 * V2 price calculation request. The legacy broker-shaped `orgId` is gone.
 *
 *   - `sellerId` / `buyerId` — scope the seller-own / buyer-own resolver
 *     lookups. Derived from auth context when omitted (carrier-context calls
 *     default to seller-own; customer-context calls default to buyer-own).
 *   - `routeId` — optional explicit Route pin (admin override or manual
 *     selection in UI). When set the resolver short-circuits to that Route.
 *   - `creatorId` — explicit creator for resolver priority. When omitted the
 *     server uses the auth context.
 *   - `vehicle` — optional; omit to resolve the Route on the lane without
 *     scoping to a vehicle type first (publish-order vehicle seeding).
 */
export const calculatePriceAPIv2Schema = calculateMultiDistancePriceAPISchema
  .omit({ vehicle: true })
  .extend({
    vehicle: vehicleTypesSchema.optional(),
    sellerId: objectIdSchema.optional(),
    buyerId: objectIdSchema.optional(),
    creatorId: objectIdSchema.optional(),
    routeId: objectIdSchema.optional(),
  });
export type CalculatePriceAPIv2 = API<
  z.infer<typeof calculatePriceAPIv2Schema>,
  CalculatePriceAPIv2Data
>;

export const calculatePriceMultiRoutesSchema = calculatePriceAPIv2Schema;
export type CalculatePriceMultiRoutesAPI = API<
  z.infer<typeof calculatePriceMultiRoutesSchema>,
  CalculatePriceMultiRoutesData
>;
